'use client';

import { useState, useTransition } from 'react';

import Link from 'next/link';

import { rejectSchemaFailure } from '@kit/next/action-result';
import { Button } from '@kit/ui/button';
import { Input } from '@kit/ui/input';
import { Textarea } from '@kit/ui/textarea';
import { Trans } from '@kit/ui/trans';

import { redeemAppSumoCode } from '../_lib/server-actions';

export function RedemptionForm({
  email,
  accounts,
}: {
  email: string;
  accounts: { id: string; name: string }[];
}) {
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  if (success)
    return (
      <div role="status" className="space-y-3">
        <h3 className="text-xl font-semibold">
          <Trans
            i18nKey="marketing:appsumo.successTitle"
            defaults="Your lifetime access is active"
          />
        </h3>
        <p>
          Your workspace is marked as AppSumo. No subscription or payment method
          is required.
        </p>
        <Link className="underline" href="/home">
          Open SupplyAutomate
        </Link>
      </div>
    );
  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        setError('');
        startTransition(async () => {
          try {
            const result = await rejectSchemaFailure(
              await redeemAppSumoCode({
                accountId: String(form.get('accountId')),
                code: String(form.get('code')),
                message: String(form.get('message') ?? ''),
              }),
            );
            if (result.success) setSuccess(true);
            else setError(result.error);
          } catch {
            setError(
              'We could not submit your code. Check the code and try again, or email help@supplyautomate.com.',
            );
          }
        });
      }}
    >
      <div>
        <label htmlFor="appsumo-email" className="mb-2 block font-medium">
          <Trans i18nKey="marketing:appsumo.email" defaults="Account email" />
        </label>
        <Input id="appsumo-email" type="email" value={email} readOnly />
      </div>
      <div>
        <label htmlFor="appsumo-account" className="mb-2 block font-medium">
          <Trans
            i18nKey="marketing:appsumo.workspace"
            defaults="Workspace to upgrade"
          />
        </label>
        <select
          id="appsumo-account"
          name="accountId"
          required
          className="border-input bg-background w-full rounded-md border p-3"
        >
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="appsumo-code" className="mb-2 block font-medium">
          <Trans
            i18nKey="marketing:appsumo.code"
            defaults="AppSumo redemption code"
          />
        </label>
        <Input
          id="appsumo-code"
          name="code"
          autoComplete="off"
          required
          minLength={35}
          maxLength={35}
          placeholder="SA-…"
        />
      </div>
      <div>
        <label htmlFor="appsumo-message" className="mb-2 block font-medium">
          <Trans
            i18nKey="marketing:appsumo.message"
            defaults="Message (optional)"
          />
        </label>
        <Textarea
          id="appsumo-message"
          name="message"
          maxLength={2000}
          placeholder="Anything you would like us to know?"
        />
      </div>
      {error ? (
        <p role="alert" className="text-destructive">
          {error}
        </p>
      ) : null}
      <Button type="submit" disabled={pending}>
        {pending ? 'Redeeming your code…' : 'Activate lifetime access'}
      </Button>
    </form>
  );
}
